/**
 * The bosses (`docs/rpg.md` §15).
 *
 * §15 is mostly a warning: a boss must not be "20 random questions", and it
 * must feel like "a culmination of a mathematical skill tree". The shape that
 * answers it is the Fraction Dragon's — stages, each one a named piece of the
 * kingdom's mathematics, climbing in difficulty — so every boss here is written
 * that way. The Number Dragon's scales are place value, its hoard is decimals,
 * and its flame is the primes.
 *
 * Three rules hold the file together, all enforced by `validateBosses`:
 *
 *   - **A stage asks its own kingdom's mathematics.** Every concept named here
 *     is `available`, sits in the boss's kingdom, and has exercise templates —
 *     so a boss question is a practice question with a dragon in front of it,
 *     generated from the same bank and graded by the same grader. The boss
 *     invents nothing (§37).
 *   - **Difficulty climbs and never falls.** Stage 1 of the Number Dragon is
 *     the difficulty of ordinary place-value practice; stage 3 is the hardest
 *     band the prime templates offer. That climb is the whole reason a boss is
 *     staged rather than shuffled.
 *   - **The defeat lines are the important ones.** They are read by a student
 *     who has just lost, and §12 asks for encouragement rather than punishment:
 *     they say what happened, they point at what to look at, and they invite
 *     the retry. Losing costs nothing anywhere in the profile.
 *
 * Rewards: 150–300 XP and 40–80 gold, paid once on the first clear.
 * `XP_LESSON_COMPLETE` is 20, so the village trial is worth eight lessons and
 * the Number Dragon twelve — a good afternoon's work either way, and nowhere
 * near enough to make the game level a measure of how many dragons somebody has
 * beaten (`docs/rpg-implementation-plan.md` §0.2). The ladder climbs with how
 * much of the curriculum a kingdom gathers up rather than with its position on
 * the map, and the Infinity Warden is the only fight paid at the ceiling
 * because there is nothing above it to save the ceiling for.
 *
 * A stage's difficulty is a promise, and `validateBosses` now holds it to that:
 * every concept a stage names must have a template that actually offers that
 * band. `bank.single` silently substitutes the nearest difficulty a template
 * does offer, so a stage marked 5 over concepts that stop at 3 is not a harder
 * stage — it is the same stage with a bigger number on the banner.
 */

import type { BossDef } from '@yuan-math/world';

/* -------------------------------------------------------------------------- */
/* Starter Village — a trial, not a monster                                   */
/* -------------------------------------------------------------------------- */

/**
 * The village has no dragon, and giving it one would be the wrong first
 * impression: this is the fight a nine-year-old meets in their first hour on
 * the platform. So the gate is kept by a person, the stakes are a road licence
 * rather than a life, and the two stages are the four operations the village
 * spent its whole quest line teaching. It is still a real assessment — three of
 * four, twice, at the difficulty the village's own practice runs at.
 */
const VILLAGE_TRIAL: BossDef = {
  id: 'boss-starter-village',
  kingdomId: 'kingdom-starter-village',
  name: 'The Gate Trial',
  emoji: '🚧',
  story: {
    intro: [
      'The road out of the village ends at a toll gate, and the gate is shut.',
      'Serla the Gatekeeper keeps the ledger for every waggon that has ever left. She will not open the gate for anyone who cannot keep it with her.',
      '"Two rounds," she says. "The ledger first, then the loading yard. Get most of it right and the road is yours."',
    ],
    victory: [
      'Serla closes the ledger and hands you the tally stick.',
      '"You can count, and you can check. That is the whole of it." The gate swings open onto the plains.',
    ],
    defeat: [
      'Serla shakes her head, but she does not take the pen away.',
      '"The gate is not going anywhere, and neither am I. Go back to the ones that caught you out and come and see me again."',
      'Nothing was lost. The road is still there tomorrow.',
    ],
  },
  stages: [
    {
      name: 'The Toll Ledger',
      conceptIds: ['multi-digit-addition', 'multi-digit-subtraction'],
      difficulty: 2,
      questionCount: 4,
    },
    {
      name: 'The Loading Yard',
      conceptIds: ['multiplication-facts', 'division-meaning', 'two-step-problems'],
      difficulty: 3,
      questionCount: 4,
    },
  ],
  reward: { xp: 160, gold: 40 },
};

/* -------------------------------------------------------------------------- */
/* Number Kingdom — the Number Dragon                                         */
/* -------------------------------------------------------------------------- */

/**
 * §15's staged shape, on this kingdom's own mathematics. Three stages, one per
 * region the kingdom is built around: the plains, the docks and the foundry.
 *
 * The last stage is difficulty 4 rather than 5 deliberately. A boss is a
 * culmination of what the kingdom taught, not a competition — the hardest band
 * a template offers is where a challenge item belongs, and finishing a kingdom
 * should not require it.
 */
const NUMBER_DRAGON: BossDef = {
  id: 'boss-number-kingdom',
  kingdomId: 'kingdom-number',
  name: 'The Number Dragon',
  emoji: '🐉',
  story: {
    intro: [
      'Past the Prime Foundry the ground turns to slag, and something very old is asleep on top of the kingdom\'s treasury.',
      'The Number Dragon does not want the gold. It wants the counting of it, and it has been arguing with the treasurers for two hundred years.',
      '"Three questions of mine," it says, without opening both eyes. "Scales, hoard, flame. Get them wrong and I shall simply go back to sleep."',
    ],
    victory: [
      'The dragon closes the ledger it was never actually reading and looks at you properly for the first time.',
      '"Place value, decimals and primes. That is my whole hoard, and now you can carry it." It stands aside from the treasury door.',
      'Behind it, the road to the Fraction Kingdom is clear.',
    ],
    defeat: [
      'The dragon settles back down, entirely unbothered.',
      '"You found the edge of what you know. That is worth more than the gold and it costs you nothing."',
      'Look again at the ideas that caught you out, and come back. The dragon keeps no score.',
    ],
  },
  stages: [
    {
      name: 'Scales of Ten',
      conceptIds: ['place-value', 'rounding', 'powers-of-ten'],
      difficulty: 2,
      questionCount: 4,
    },
    {
      name: 'The Hoard Divided',
      conceptIds: ['decimal-place-value', 'compare-decimals', 'add-decimals', 'multiply-decimals'],
      difficulty: 3,
      questionCount: 4,
    },
    /*
     * Five questions over five concepts, so the climax asks each exactly once
     * rather than doubling back. That is not only tidier: at difficulty 4 the
     * prime-factorisation templates span about ten distinct items, so a stage
     * asking that concept twice would be leaning on the engine's duplicate
     * guard to keep the same question off the screen twice.
     */
    {
      name: 'The Prime Flame',
      conceptIds: ['prime-factorization', 'gcf-lcm', 'prime-composite', 'factors-multiples', 'integers'],
      difficulty: 4,
      questionCount: 5,
    },
  ],
  reward: { xp: 240, gold: 60 },
};

/* -------------------------------------------------------------------------- */
/* Fraction Kingdom — the Fraction Dragon                                     */
/* -------------------------------------------------------------------------- */

/**
 * §15's own example, staged exactly as it is written there: fraction addition,
 * then fraction multiplication, then a multi-step problem that needs both.
 *
 * The third stage is the Ratio Market rather than more of the kitchens, because
 * "multi-step word problem involving fractions" is what a rate, a proportion and
 * a percentage each are — and because a dragon that only ever asks for a sum of
 * eighths has not culminated anything.
 */
const FRACTION_DRAGON: BossDef = {
  id: 'boss-fraction-kingdom',
  kingdomId: 'kingdom-fraction',
  name: 'The Fraction Dragon',
  emoji: '🐉',
  story: {
    intro: [
      'The road above the Ratio Market ends on a ledge of broken plates, and the plates are enormous.',
      'The Fraction Dragon has eaten from every kitchen in the kingdom and paid for none of it, on the grounds that nobody has ever cut it a share it agreed with.',
      '"Three helpings," it says. "Add them, take a part of a part, and then price the lot. Cut me short and I shall come down and eat the market."',
    ],
    victory: [
      'The dragon counts its share twice, which is what dragons do, and finds it correct on both counts.',
      '"A half, then a half of a half, and what the whole thing is worth by Thursday. You have the kingdom in your hands." It settles down to eat, quite politely.',
      'The road east to the Algebra Kingdom is open.',
    ],
    defeat: [
      'The dragon pushes the plate aside, entirely unoffended.',
      '"Somewhere in there a denominator got away from you. Go and find out which one, and bring it back with you."',
      'Nothing was lost, and the tables are laid again tomorrow.',
    ],
  },
  stages: [
    {
      name: 'The Uneven Helping',
      conceptIds: ['add-like-fractions', 'add-fractions', 'common-denominators'],
      difficulty: 2,
      questionCount: 4,
    },
    {
      name: 'A Part of a Part',
      conceptIds: ['multiply-fractions', 'fraction-times-whole', 'divide-fractions'],
      difficulty: 3,
      questionCount: 4,
    },
    {
      name: 'The Price of the Hoard',
      conceptIds: ['proportions', 'unit-rates', 'percentages', 'percent-applications'],
      difficulty: 4,
      questionCount: 4,
    },
  ],
  reward: { xp: 200, gold: 50 },
};

/* -------------------------------------------------------------------------- */
/* Algebra Kingdom — the Quadratic King                                       */
/* -------------------------------------------------------------------------- */

/**
 * §5 offers three names for this kingdom's bosses; the Quadratic King is the
 * one that can hold the whole of it, because his castle is where the chain
 * ends. The stages are the kingdom's three moves: write it, balance it, and
 * then break it open — and the last one is the only place where two answers is
 * the right number of answers.
 */
const QUADRATIC_KING: BossDef = {
  id: 'boss-algebra-kingdom',
  kingdomId: 'kingdom-algebra',
  name: 'The Quadratic King',
  emoji: '👑',
  story: {
    intro: [
      'The throne room of Quadratic Castle has two thrones, and the same king is sitting in both of them.',
      '"Every question I ask has two answers," says the Quadratic King. "So does every question worth asking. Bring me both and the castle is yours."',
      'Three rounds: the court of letters, the bridge that has to balance, and the two doors at the far end.',
    ],
    victory: [
      'The king vacates one throne, and then the other.',
      '"Expression, equation, factor, formula. You went the whole way round my castle and did not lose a sign once." The keys change hands without ceremony.',
      'The mountain road and the coast road both leave from the castle gate.',
    ],
    defeat: [
      'The king waves the court away without getting up.',
      '"A sign, or a second root you did not write down. Neither is a verdict on you — find out which it was."',
      'Nothing was taken. The court sits again whenever you do.',
    ],
  },
  stages: [
    {
      name: 'The Court of Letters',
      conceptIds: ['linear-expressions', 'algebraic-expressions', 'exponent-rules'],
      difficulty: 2,
      questionCount: 4,
    },
    {
      name: 'The Bridge That Balances',
      conceptIds: ['multi-step-equations', 'literal-equations', 'one-variable-inequalities'],
      difficulty: 3,
      questionCount: 4,
    },
    {
      name: 'Two Doors, One Formula',
      conceptIds: ['factoring', 'quadratics', 'completing-the-square', 'quadratic-formula'],
      difficulty: 4,
      questionCount: 4,
    },
  ],
  reward: { xp: 240, gold: 60 },
};

/* -------------------------------------------------------------------------- */
/* Geometry Kingdom — the Compass Golem                                       */
/* -------------------------------------------------------------------------- */

/**
 * A construct rather than a creature, because this kingdom's whole argument is
 * that a thing is not true until somebody has given a reason for it: the golem
 * is the reason, walking. Its three stages are the kingdom's three demands in
 * the order the chain met them — measure it, prove it, and then construct it.
 */
const COMPASS_GOLEM: BossDef = {
  id: 'boss-geometry-kingdom',
  kingdomId: 'kingdom-geometry',
  name: 'The Compass Golem',
  emoji: '🗿',
  story: {
    intro: [
      'At the head of the coast road stands a figure of jointed stone with a compass for one arm and a straightedge for the other.',
      'The Compass Golem was built to check the kingdom\'s work, and it has been refusing surveys for four hundred years.',
      '"Measure. Prove. Then draw me the circle," it says. "In that order. I do not accept an answer I cannot construct."',
    ],
    victory: [
      'The golem draws one arc, sets the straightedge down, and steps off the road.',
      '"Measured, argued and constructed. The record will take it." Then it goes still, which is how it says yes.',
    ],
    defeat: [
      'The golem lowers the compass and waits, which it is extremely good at.',
      '"Either the reason was missing or the measurement was. Go back to whichever one it turns out to be."',
      'Nothing was struck from the record. The survey can be filed again tomorrow.',
    ],
  },
  stages: [
    {
      name: 'The Measured Court',
      conceptIds: ['perimeter', 'area-rectangles', 'angle-measure'],
      difficulty: 2,
      questionCount: 4,
    },
    {
      name: 'The Proved Arch',
      conceptIds: ['angle-relationships', 'triangle-properties', 'parallel-lines', 'pythagoras'],
      difficulty: 3,
      questionCount: 4,
    },
    {
      name: 'The Turning Circle',
      conceptIds: ['circles', 'arc-sector', 'coordinate-distance', 'similarity'],
      difficulty: 4,
      questionCount: 4,
    },
  ],
  reward: { xp: 250, gold: 60 },
};

/* -------------------------------------------------------------------------- */
/* Function Kingdom — the Function Engine                                     */
/* -------------------------------------------------------------------------- */

/**
 * The machine the foundry was built around. Its stages are the three things a
 * function kingdom actually asks — what goes in, what the machine does to it,
 * and what happens when the thing it does outruns every straight line.
 */
const FUNCTION_ENGINE: BossDef = {
  id: 'boss-function-kingdom',
  kingdomId: 'kingdom-function',
  name: 'The Function Engine',
  emoji: '⚙️',
  story: {
    intro: [
      'Below the foundry floor is the machine the foundry was built around, and nobody currently alive has seen all of it.',
      'The Function Engine takes a number in at one end. What it does in the middle is the entire argument.',
      '"Hopper, gearbox, lamp," it prints onto a card. "Three rounds. Tell me what comes out, and do not guess."',
    ],
    victory: [
      'The Engine prints one last card and stops the belt.',
      '"In, through, out. You read the whole machine." Underneath the card is a key to the mountain road.',
    ],
    defeat: [
      'The belt slows and the Engine waits with the hopper standing open.',
      '"Something went in that you had not tested. Go back to that one machine on its own, then come and run the line again."',
      'Nothing jammed and nothing broke. It starts up again whenever you do.',
    ],
  },
  stages: [
    {
      name: 'The Input Hopper',
      conceptIds: ['functions-intro', 'linear-functions', 'domain-and-range'],
      difficulty: 2,
      questionCount: 4,
    },
    {
      name: 'The Gearbox',
      conceptIds: ['function-operations', 'function-transformations', 'inverse-functions', 'quadratic-graphs'],
      difficulty: 3,
      questionCount: 4,
    },
    {
      name: 'The Lighthouse Lamp',
      conceptIds: ['exponential-functions', 'logarithms', 'exponential-equations', 'series'],
      difficulty: 4,
      questionCount: 4,
    },
  ],
  reward: { xp: 260, gold: 65 },
};

/* -------------------------------------------------------------------------- */
/* Trigonometry Peaks — the Wave Titan                                        */
/* -------------------------------------------------------------------------- */

/**
 * Two stages at difficulty 4 rather than a climb to 5, for the reason the
 * Number Dragon stops at 4: the hardest band a template offers is where a
 * challenge item belongs, and finishing a kingdom should not require it.
 *
 * The first stage is difficulty 3 rather than 2 because the basecamp concepts
 * have no easier band to be asked at — `trig-ratios` and `trig-applications`
 * start at 3. A stage marked 2 here would have been asked at 3 regardless,
 * which is the silent mismatch `validateBosses` now refuses.
 *
 * Paid at 200 rather than the 260 its position would suggest, and its quest
 * line is trimmed to match. Trigonometry Peaks is the smallest kingdom on the
 * map — ten concepts against Geometry's forty-four — so a boss reward set by
 * how high up the map it sits would pay nearly what learning the whole kingdom
 * pays. Reward tracks the size of the story, not the altitude of the mountain;
 * `quests.test.ts` holds every kingdom to it.
 */
const WAVE_TITAN: BossDef = {
  id: 'boss-trigonometry-kingdom',
  kingdomId: 'kingdom-trigonometry',
  name: 'The Wave Titan',
  emoji: '🌊',
  story: {
    intro: [
      'Identity Ridge ends at a sleeping figure the size of the mountain, and the mountain breathes.',
      'The Wave Titan has one heartbeat and it repeats for ever: up, down, and up again, exactly the same each time round.',
      '"Match me," it says, without waking. "The triangle, the wheel, and the statements that hold for every angle there is."',
    ],
    victory: [
      'The breathing steadies, which from the Titan counts as applause.',
      '"Ratio, rotation, identity — and you never once mistook a thing true for every angle for a thing true only for yours."',
      'For the first time the far side of the range is visible, and there is a kingdom on it.',
    ],
    defeat: [
      'The Titan breathes on, no faster and no slower.',
      '"An angle in the wrong quadrant, or an identity used somewhere it was not standing. Neither one is a fall. Come back up."',
      'The rope held, and the peaks are not going anywhere.',
    ],
  },
  stages: [
    {
      name: 'Three Sides and a Name',
      conceptIds: ['trig-ratios', 'trig-applications'],
      difficulty: 3,
      questionCount: 4,
    },
    {
      name: 'The Turning Wheel',
      conceptIds: ['unit-circle', 'tangent-function', 'trig-graphs'],
      difficulty: 4,
      questionCount: 4,
    },
    {
      name: 'The Ridge That Always Holds',
      conceptIds: ['trig-identities', 'trig-addition-formulas', 'trig-equations', 'law-of-sines-cosines'],
      difficulty: 4,
      questionCount: 4,
    },
  ],
  reward: { xp: 200, gold: 50 },
};

/* -------------------------------------------------------------------------- */
/* Calculus Kingdom — the Infinity Warden                                     */
/* -------------------------------------------------------------------------- */

/**
 * The one fight on the map whose *form* is decided by the grader.
 *
 * `areExpressionsEquivalent` parses sums, products and powers of single letters
 * and knows no `sin`, no `ln` and no `e` (`CLAUDE.md`), so an antiderivative
 * like −cos³x/3 + cos⁵x/5 cannot be typed into a box, let alone marked. Every
 * stage here therefore asks for a **value** — a definite integral, a rate, a
 * radius of convergence — or for a **choice**: which substitution this integral
 * becomes, which test settles this series. That is a restriction on the
 * questions and not on the mathematics: the definite integral of x(x²+3)² is a
 * rational number, and asking for it tests the same peel-and-substitute.
 *
 * The Warden is written to say so out loud — it asks for numbers and refuses
 * descriptions — so the boss's own voice is the reason the questions look the
 * way they do, rather than the limitation showing through as an apology.
 *
 * Paid at the ceiling of the band, 300 and 80: this is the top of the map, and
 * there is no fight above it to save the ceiling for.
 */
const INFINITY_WARDEN: BossDef = {
  id: 'boss-calculus-kingdom',
  kingdomId: 'kingdom-calculus',
  name: 'The Infinity Warden',
  emoji: '♾️',
  story: {
    intro: [
      'Past the last telescope the ground stops, the Infinity Realm starts, and something is keeping the gate.',
      'The Infinity Warden asks for numbers. It does not accept a description of a method, a sketch of a curve, or a promise about what you would do given longer.',
      '"A value, or a choice," it says. "How fast, how much, and which test. Three rounds, and I count exactly."',
    ],
    victory: [
      'The Warden totals the three rounds and finds the total finite.',
      '"Approached, differentiated, accumulated — and then summed without end, and it still came to a number." The gate is not so much opened as withdrawn.',
      'Behind it the map simply stops, which is the honest edge of what has been drawn so far.',
    ],
    defeat: [
      'The Warden sets the tally down.',
      '"A limit taken at the wrong point, a constant left behind, or a test used on a series it does not fit. Find which, and count it again."',
      'Nothing was lost. Infinity is in no particular hurry.',
    ],
  },
  stages: [
    {
      name: 'The Gate Approached',
      conceptIds: ['limits', 'continuity', 'rates-of-change'],
      difficulty: 3,
      questionCount: 4,
    },
    {
      name: 'The Falling Stone',
      conceptIds: ['derivative-rules', 'derivative-applications', 'optimization', 'motion-derivatives'],
      difficulty: 4,
      questionCount: 4,
    },
    /*
     * Five questions over five concepts, so the climax asks each exactly once —
     * the same reasoning as the Prime Flame, and the same reason it is not six.
     */
    {
      name: 'The Endless Tally',
      conceptIds: ['u-substitution', 'fundamental-theorem', 'series-convergence', 'ratio-test', 'taylor-series'],
      difficulty: 4,
      questionCount: 5,
    },
  ],
  reward: { xp: 300, gold: 80 },
};

/* -------------------------------------------------------------------------- */
/* Statistics Kingdom — the Null Hypothesis                                   */
/* -------------------------------------------------------------------------- */

/**
 * The only boss on the map a student does not beat by being right.
 *
 * A statistics kingdom whose final fight was a monster would be teaching the
 * wrong lesson twice over, because the thing standing between a student and a
 * conclusion here is never a creature — it is the possibility that what they
 * found is the sample being the sample. So the boss is that possibility,
 * personified and entirely reasonable, and the victory line is careful to say
 * what was actually achieved: rejected at a level, on this evidence, and back
 * the moment either changes. Telling a child they had *proved* the null false
 * would be a fantasy the mathematics does not support (§37), and this is
 * exactly the sort of place that slips.
 *
 * Its stages walk the kingdom's own argument: describe the crowd, count what
 * has not happened yet, and then let the court decide what a sample is entitled
 * to say about everyone who was not asked.
 */
const NULL_HYPOTHESIS: BossDef = {
  id: 'boss-statistics-kingdom',
  kingdomId: 'kingdom-statistics',
  name: 'The Null Hypothesis',
  emoji: '⚖️',
  story: {
    intro: [
      'The Inference Court has one permanent defendant, and it never speaks first.',
      'The Null Hypothesis maintains that nothing whatever is happening: the coin is fair, the treatment does nothing, and the difference you found is the sample being a sample.',
      '"You may not prove me wrong," it says, pleasantly. "You may only show that what you found would be a very strange thing to find if I were right. Three rounds."',
    ],
    victory: [
      'The court finds the evidence strong enough, and says precisely that and nothing more.',
      '"Rejected at your level, on your evidence, and I shall be back the moment either of those changes. That is not a defeat. That is how the thing works."',
      'The Statistics Kingdom is yours, on the balance of probabilities.',
    ],
    defeat: [
      'The Null Hypothesis stands — which, it points out, is not at all the same as being true.',
      '"You did not fail to find it. You failed to show it, and those are different things. Go back to the counting and bring more of it."',
      'Nothing was lost, and the court sits again on Thursday.',
    ],
  },
  stages: [
    {
      name: 'The Shape of the Crowd',
      conceptIds: ['mean-median-mode', 'histograms', 'data-variability', 'scatter-plots'],
      difficulty: 2,
      questionCount: 4,
    },
    {
      name: 'Counted Before It Happens',
      conceptIds: ['basic-probability', 'compound-probability', 'conditional-probability', 'independence'],
      difficulty: 3,
      questionCount: 4,
    },
    {
      name: 'The Court Sits',
      conceptIds: [
        'expected-value',
        'binomial-distribution',
        'sampling-distributions',
        'confidence-intervals',
        'hypothesis-testing',
      ],
      difficulty: 4,
      questionCount: 5,
    },
  ],
  reward: { xp: 250, gold: 60 },
};

/* -------------------------------------------------------------------------- */
/* Linear Algebra Kingdom — the Unturning                                     */
/* -------------------------------------------------------------------------- */

/**
 * The fight at the top of the other road.
 *
 * Its three stages are the kingdom's three objects in the order it built them —
 * the arrow, the grid, and the directions the grid cannot rotate — and the last
 * one is the whole reason the spire is the tallest thing here: an eigenvector
 * is not a harder calculation than a row reduction, it is the answer to a
 * different question, and the fight is staged so that the student arrives at it
 * having already done the reduction it depends on.
 *
 * Its victory line says the thing `kingdoms.ts` and `linear-algebra.test.ts`
 * both exist to protect: nothing up here leans on a limit, a derivative or an
 * integral, and a student who came this way has not taken the easier road —
 * they have taken the other one.
 */
const THE_UNTURNING: BossDef = {
  id: 'boss-linear-algebra-kingdom',
  kingdomId: 'kingdom-linear-algebra',
  name: 'The Unturning',
  emoji: '✴️',
  story: {
    intro: [
      'The Eigen Spire narrows to a single room where every arrow on the walls points outwards, and not one of them has ever moved.',
      'The Unturning is what a transformation looks like from the inside, standing in the only directions it cannot rotate.',
      '"Three rounds," it says, from all of those directions at once. "The harbour, the grid, and then the numbers I am actually made of."',
    ],
    victory: [
      'The room simplifies. What was a tangle of rows is a short diagonal, and every number on it is a stretch along a direction you can name.',
      '"Vector, matrix, eigenvalue. You have the whole spire and it fits on one line."',
      'From up here the Calculus Kingdom is visible along the other road — and neither of the two was ever the way to the other.',
    ],
    defeat: [
      'The arrows on the walls do not so much as flicker.',
      '"A row operation that changed the answer, or a direction that turned after all. Go back down to the grid and come up again."',
      'Nothing was lost. The spire has stood a long time and is in no hurry.',
    ],
  },
  stages: [
    {
      name: 'The Harbour Reckoning',
      conceptIds: ['vectors', 'dot-product', 'linear-combinations'],
      difficulty: 2,
      questionCount: 4,
    },
    {
      name: 'The Grid Reduced',
      conceptIds: ['matrices', 'row-reduction', 'gaussian-elimination', 'determinants'],
      difficulty: 3,
      questionCount: 4,
    },
    {
      name: 'The Fixed Directions',
      conceptIds: ['eigenvalues', 'eigenvectors', 'diagonalization', 'basis', 'linear-transformations'],
      difficulty: 4,
      questionCount: 5,
    },
  ],
  reward: { xp: 240, gold: 60 },
};

/**
 * Every boss on the map.
 *
 * Ten of ten, in map order. That is a milestone rather than a rule: a kingdom
 * may have no boss, and one that does not is `cleared` when there is nothing
 * left in it to learn. What is not allowed is the other direction — a kingdom
 * naming a boss nobody defined is a seal with no key, and `validateBosses`
 * refuses it.
 */
export const BOSSES: BossDef[] = [
  VILLAGE_TRIAL,
  NUMBER_DRAGON,
  FRACTION_DRAGON,
  QUADRATIC_KING,
  COMPASS_GOLEM,
  THE_UNTURNING,
  FUNCTION_ENGINE,
  NULL_HYPOTHESIS,
  WAVE_TITAN,
  INFINITY_WARDEN,
];
